import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import arjenTeotAsset from "@/assets/montessori-arjen-teot.png.asset.json";
import yhdessaTekemisestaAsset from "@/assets/montessori-yhdessa-tekemisesta.png.asset.json";

const SLUG = "pienet-muutokset-kotona-itsenaisyys";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Pienet muutokset kotona — suuri vaikutus lapsen itsenäisyyteen";
const DESC = "Miten pienet Montessori-muutokset kotona kasvattavat lapsen itsenäisyyttä, itseluottamusta ja oppimisen iloa. 5 käytännön askelta suomalaiselle perheelle.";
const PUBLISHED = "2026-07-29";
const HERO = articleHeroFor(SLUG);
const OG_IMAGE = `https://montessori24.fi${arjenTeotAsset.url}`;

export const Route = createFileRoute("/montessori-koulu/pienet-muutokset-kotona-itsenaisyys")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Arjen Montessori"
      title={TITLE}
      lead="Moni suomalainen vanhempi haluaa kasvattaa lapsestaan rohkean, omatoimisen ja itseensä luottavan ihmisen. Usein ajatellaan, että tähän tarvitaan erityisiä opetusmenetelmiä, kalliita välineitä tai täydellisesti suunniteltu koti. Montessori-filosofiassa totuus on paljon yksinkertaisempi: lapsi oppii parhaiten osallistumalla oikeaan elämään."
      readingMinutes={7}
      publishedISO={PUBLISHED}
    >
      <figure>
        <img
          src={arjenTeotAsset.url}
          alt="Lapsi taittelee pyykkejä — pienet arjen teot rakentavat suuria taitoja."
          loading="lazy"
          decoding="async"
          width={906}
          height={1500}
        />
        <figcaption>Pienet arjen teot rakentavat suuria taitoja — itseluottamusta, itsenäisyyttä ja käytännön elämäntaitoja.</figcaption>
      </figure>

      <p>Pienillä arjen muutoksilla voit antaa lapsellesi mahdollisuuden harjoitella taitoja, jotka kantavat pitkälle aikuisuuteen. Montessori24.fi:n tavoitteena on auttaa suomalaisia perheitä luomaan kotiympäristö, jossa lapsen luonnollinen uteliaisuus, itsenäisyys ja oppimisen ilo pääsevät kukoistamaan.</p>

      <h2>1. Tuo arki lapsen ulottuville</h2>
      <p>Kun lapsi pystyy tekemään asioita itse, hänen ei tarvitse odottaa jatkuvasti aikuisen apua. Voit aloittaa esimerkiksi näin:</p>
      <ul>
        <li>Sijoita kirjat matalalle hyllylle.</li>
        <li>Ripusta takki lapsen korkeudelle.</li>
        <li>Aseta kengät helposti saataville.</li>
        <li>Järjestä muutama lelu tai tekeminen lapsen nähtäville ja ulottuville.</li>
      </ul>
      <p>Kun ympäristö tukee itsenäisyyttä, lapsi käyttää luonnollista haluaan oppia. Lapsen oma <Link to="/vaaterekki">vaaterekki</Link> ja matalat hyllyt ovat tähän hyvä alku.</p>

      <h2>2. Anna lapselle oikeita, lapsen kokoisia työvälineitä</h2>
      <p>Lapset rakastavat oikeita tehtäviä. Sen sijaan, että kaikki olisi leikkiä, lapsi haluaa tehdä samoja asioita kuin aikuiset. Hyviä esimerkkejä ovat:</p>
      <ul>
        <li>pieni harja ja rikkalapio</li>
        <li>lapsen käteen sopiva kannu veden kaatamiseen</li>
        <li>pieni kastelukannu kasveille</li>
        <li>turvallinen lasten veitsi pehmeiden hedelmien pilkkomiseen</li>
      </ul>
      <p>Kun väline on oikean kokoinen, onnistuminen on helpompaa ja motivaatio kasvaa. <Link to="/montessori-lelut">Montessori24.fi:n valikoima</Link> on suunniteltu juuri tähän tarkoitukseen — välineisiin, joiden avulla lapsi voi osallistua turvallisesti kodin arkeen.</p>

      <h2>3. Tarjoa rajattuja valintoja</h2>
      <p>Itsenäisyys ei tarkoita sitä, että lapsi päättää kaikesta. Montessori suosittelee antamaan lapselle kaksi hyvää vaihtoehtoa. Esimerkiksi:</p>
      <ul>
        <li>"Haluatko punaisen vai sinisen paidan?"</li>
        <li>"Kävelemmekö vai mennäänkö rattailla?"</li>
        <li>"Haluatko omenan vai banaanin välipalaksi?"</li>
      </ul>
      <p>Näin lapsi saa harjoitella päätöksentekoa turvallisissa rajoissa.</p>

      <h2>4. Hidasta hieman</h2>
      <p>Arki on usein kiireistä. Silti juuri kiire estää lasta harjoittelemasta uusia taitoja. Kun varaat muutaman ylimääräisen minuutin, lapsi voi:</p>
      <ul>
        <li>pukea itse</li>
        <li>pestä kädet rauhassa</li>
        <li>kaataa maidon omaan lasiin</li>
        <li>siivota omat jälkensä</li>
      </ul>
      <p>Vaikka tekeminen kestää aluksi pidempään, harjoittelu tekee lapsesta vähitellen yhä taitavamman. Montessori-ajattelussa tärkeintä ei ole nopeus, vaan oppimisprosessi.</p>

      <h2>5. Ota lapsi mukaan kodin askareisiin</h2>
      <figure>
        <img
          src={yhdessaTekemisestaAsset.url}
          alt="Lapsi imuroi kotona — ei ole kyse siivoamisesta, vaan yhdessä tekemisestä."
          loading="lazy"
          decoding="async"
          width={906}
          height={1500}
        />
        <figcaption>Ei ole kyse siivoamisesta, vaan yhdessä tekemisestä. Lapsi muistaa, että sai auttaa ja olla osa perhettä.</figcaption>
      </figure>
      <p>Lapset eivät yleensä halua vain katsella sivusta — he haluavat osallistua. Anna lapsen esimerkiksi:</p>
      <ul>
        <li>pestä kasviksia</li>
        <li>sekoittaa taikinaa</li>
        <li>kattaa pöytä</li>
        <li>kastella kasvit</li>
        <li>lajitella pyykit</li>
        <li>pyyhkiä pöytää</li>
        <li>taitella pieniä pyyhkeitä tai sukkia</li>
      </ul>
      <p>Nämä eivät ole vain kotitöitä — ne kehittävät keskittymistä, hienomotoriikkaa, vastuuntuntoa ja itseluottamusta. <Link to="/oppimistorni">Oppimistorni</Link> tuo pienimmätkin turvallisesti keittiön työtasolle mukaan.</p>

      <h2>Entä jos lapsi tekee hitaasti tai sotkee?</h2>
      <p>Se kuuluu oppimiseen. Ensimmäisillä kerroilla vettä voi läikkyä, sukat voivat mennä väärin päin ja murusia jää pöydälle. Jokainen yritys kehittää taitoja enemmän kuin se, että aikuinen tekee kaiken lapsen puolesta. Virheet eivät ole epäonnistumisia — ne ovat osa oppimista.</p>

      <h2>Pienet muutokset riittävät</h2>
      <p>Montessori-kodin ei tarvitse näyttää täydelliseltä eikä kaikkea tarvitse muuttaa kerralla. Usein riittää, että aloitat yhdestä pienestä asiasta:</p>
      <ul>
        <li>siirrät kirjat lapsen korkeudelle</li>
        <li>hankit pienen harjan</li>
        <li>annat lapsen kaataa oman juomansa</li>
        <li>pyydät häntä auttamaan päivällisen valmistuksessa</li>
      </ul>
      <p>Juuri nämä pienet hetket rakentavat vähitellen lapsen uskoa omiin kykyihinsä.</p>

      <h2>Lopuksi</h2>
      <p>Itsenäisyys ei synny käskemällä, vaan antamalla lapselle mahdollisuuksia harjoitella. Kun lapsi saa osallistua arkeen, hän oppii enemmän kuin vain käytännön taitoja. Hän oppii, että hänen panoksellaan on merkitystä, että hän pystyy oppimaan uutta ja että hän on tärkeä osa perhettä.</p>
      <p>Montessori24.fi uskoo, että jokainen koti voi tukea lapsen kasvua Montessori-periaatteiden mukaisesti — ilman suuria remontteja tai kalliita hankintoja. Pienet, harkitut muutokset arjessa voivat tehdä yllättävän suuren eron lapsen itsenäisyydelle, itseluottamukselle ja oppimisen ilolle.</p>
      <p><Link to="/kauppa">Tutustu Montessori24.fi:n valikoimaan →</Link> ja löydä käytännöllisiä Montessori-tuotteita, jotka auttavat tekemään arjesta sujuvampaa sekä lapselle että koko perheelle.</p>
    </BlogPost>
  );
}